import { Component, Element, Host, Prop, h, Watch } from '@stencil/core';
import { SelectedAriaAttributes, parseAndGetAriaAttributes } from '../../utils';
import { TIcon } from '../../svg/icons';
import {
  LINKBUTTON_VARIANTS,
  TLinkAriaAttribute,
  TLinkButtonAlign,
  TLinkButtonSize,
  TLinkButtonVariant,
} from './link-button-utils';

@Component({
  tag: 'dc-ui-link-button',
  styleUrl: 'dc-ui-link-button.scss',
  shadow: true,
})
export class DcUiLinkButton {
  @Element() host: HTMLElement;
  link: HTMLAnchorElement;

  /** Adds a size to the link button. Default value is 'normal'.*/
  @Prop() size?: TLinkButtonSize = 'normal';
  /** Adds a variant to the link button. Default value is 'secondary'. */
  @Prop() variant?: TLinkButtonVariant = 'secondary';
  /** Adds the href attr to the link button. No default value is set. */
  @Prop() href: string;
  /** Adds the target attr to the link button. Default value is _self. */
  @Prop() target?: string = '_self';
  /** Adds an icon to the link button. No default value is an empty string. */
  @Prop() icon?: TIcon;
  /** Adds alternatively an icon via string to the link button. Default value is an empty string. */
  @Prop() iconSource?: string;
  /** Adds prop download to the link button. Default value is false. */
  @Prop() download?: boolean = false;
  /** Hides the label on the button. Default value is false. */
  @Prop() hideLabel = false;
  /** Adds prop disabled to the link button. Default value is false. */
  @Prop({ reflect: true }) disabled?: boolean = false;
  /** Set the host and his child to inline or block element. Default value is true. */
  @Prop() inline?: boolean = true;
  /** Adds an alignment to the link button. Default value is 'center'. */
  @Prop() align?: TLinkButtonAlign = 'left';
  /** Adds aria prop object to the link button. Default value is an empty string. */
  @Prop() aria?: SelectedAriaAttributes<TLinkAriaAttribute>;

  isComponentValid = true;

  @Watch('variant')
  validateVariant(variant: TLinkButtonVariant) {
    if (!LINKBUTTON_VARIANTS.includes(variant)) {
      this.isComponentValid = false;
      throw new Error(`Invalid variant: ${variant}`);
    }
  }

  @Watch('href')
  checkForHref() {
    if (!this.href || this.href === '') {
      this.disabled = true;
    }
  }

  componentWillLoad() {
    this.checkForHref();
    this.validateVariant(this.variant);
  }

  render() {
    const hrefAttribute = !this.disabled ? { href: this.href } : {};
    const downloadAttribute = this.download ? { download: this.download } : {};
    const disabledAttribute = this.disabled ? { ariaDisabled: this.disabled, role: 'link' } : {};

    if (this.isComponentValid) {
      return (
        <Host
          class={`
            ${this.inline ? 'inline' : ''}
          `}>
          <a
            class={`
              dc-ui-link-button
              dc-ui-link-button--${this.size}
              dc-ui-link-button--${this.variant}
              ${this.hideLabel && this.icon ? 'dc-ui-link-button--hide-label' : ''}
              ${this.disabled ? 'disabled' : ''}
              ${this.align ? `dc-ui-link-button--${this.align}` : ''}
              ${this.icon ? 'dc-ui-link-button--icon' : ''}
            `}
            {...hrefAttribute}
            {...downloadAttribute}
            {...disabledAttribute}
            {...parseAndGetAriaAttributes(this.aria)}
            target={this.target}>
            {this.icon || this.iconSource ? (
              <dc-ui-icon
                aria-hidden="true"
                name={this.icon}
                source={this.iconSource}
                size={this.size === 'normal' ? 'medium' : this.size}></dc-ui-icon>
            ) : null}

            <span>
              <slot />
            </span>
          </a>
        </Host>
      );
    }
  }
}
